/**
 * How an AI agent looks in the office (docs/refactor/25-agentes-no-escritorio.md) — appearance only, never what
 * the agent can do: a coding agent may well be the GuardDragon.
 *
 * One system, three families of interchangeable looks: Robots, Orbs and Mechatronic Pets. Each family is a list
 * of models and a palette; a new family is one more entry in FAMILIES (and its drawing in AgentFigures.tsx).
 *
 * Agents come and go all the time (every conversation, every tab, every part of a team task), so the choice
 * lives in the person's preference — the owner's rules (08/10/2026):
 *   - nothing chosen: a random family, model and colour per agent;
 *   - one family: random within it;
 *   - fixed: every agent of that person looks the same.
 * "Random" is stable per agent: seeded by the agent's id, it does not change between refreshes.
 */

import { HATS, type HatKind } from './wardrobe'

export type AgentFamily = 'robot' | 'orb' | 'pet'

export interface AgentLook {
  family: AgentFamily
  model: string
  color: string
  /** The hat the person chose for their agents (docs/refactor/47): the people's hats, drawn small. */
  hat?: HatKind
}

export interface AgentLookPreference {
  mode: 'random' | 'family' | 'fixed'
  family?: AgentFamily
  look?: AgentLook
  hat?: HatKind
}

/** What a model carries: the accessory that hints at a trade, and — for pets — the animal. */
export interface AgentModel {
  id: string
  name: string
  accessory: 'laptop' | 'paper' | 'magnifier' | 'wrench' | 'shield' | 'chart' | 'palette' | 'gear'
  /** Its own colour, the default. */
  color: string
  species?: 'dog' | 'cat' | 'fox' | 'turtle' | 'owl' | 'bunny' | 'panda' | 'dragon' | 'salamander'
}

/** The colours every family can wear, soft like the rest of the office. */
export const AGENT_COLORS: Record<string, string> = {
  blue: '#5B9BF0',
  amber: '#F2B441',
  green: '#5CC98A',
  red: '#E5625C',
  violet: '#9B7BEA',
  pink: '#E88BC4',
  cyan: '#4FC8D8',
  white: '#E9EEF6',
}

const COLOR_IDS = Object.keys(AGENT_COLORS)

export const FAMILIES: Record<AgentFamily, { models: AgentModel[] }> = {
  robot: {
    models: [
      { id: 'devbot', name: 'DevBot', accessory: 'laptop', color: 'blue' },
      { id: 'docbot', name: 'DocBot', accessory: 'paper', color: 'amber' },
      { id: 'testbot', name: 'TestBot', accessory: 'magnifier', color: 'green' },
      { id: 'opsbot', name: 'OpsBot', accessory: 'wrench', color: 'amber' },
      { id: 'secbot', name: 'SecBot', accessory: 'shield', color: 'red' },
      { id: 'databot', name: 'DataBot', accessory: 'chart', color: 'violet' },
      { id: 'uibot', name: 'UiBot', accessory: 'palette', color: 'pink' },
      { id: 'autobot', name: 'AutoBot', accessory: 'gear', color: 'cyan' },
    ],
  },
  orb: {
    models: [
      { id: 'codeorb', name: 'CodeOrb', accessory: 'laptop', color: 'blue' },
      { id: 'docuorb', name: 'DocuOrb', accessory: 'paper', color: 'amber' },
      { id: 'testorb', name: 'TestOrb', accessory: 'magnifier', color: 'green' },
      { id: 'opsorb', name: 'OpsOrb', accessory: 'wrench', color: 'red' },
      { id: 'dataorb', name: 'DataOrb', accessory: 'chart', color: 'violet' },
      { id: 'uiorb', name: 'UiOrb', accessory: 'palette', color: 'pink' },
      { id: 'planorb', name: 'PlanOrb', accessory: 'paper', color: 'cyan' },
      { id: 'guardorb', name: 'GuardOrb', accessory: 'shield', color: 'red' },
    ],
  },
  pet: {
    models: [
      { id: 'codepup', name: 'CodePup', accessory: 'laptop', color: 'blue', species: 'dog' },
      { id: 'docucat', name: 'DocuCat', accessory: 'paper', color: 'amber', species: 'cat' },
      { id: 'testfox', name: 'TestFox', accessory: 'magnifier', color: 'green', species: 'fox' },
      { id: 'opsturtle', name: 'OpsTurtle', accessory: 'wrench', color: 'amber', species: 'turtle' },
      { id: 'dataowl', name: 'DataOwl', accessory: 'chart', color: 'violet', species: 'owl' },
      { id: 'uibunny', name: 'UiBunny', accessory: 'palette', color: 'pink', species: 'bunny' },
      { id: 'planpanda', name: 'PlanPanda', accessory: 'paper', color: 'white', species: 'panda' },
      { id: 'guarddragon', name: 'GuardDragon', accessory: 'shield', color: 'red', species: 'dragon' },
      // The house animal joins the cast (docs/refactor/68): the same creature as the mark, but drawn
      // as a pet, in the office's soft palette - never in the brand amber.
      { id: 'forgesalamander', name: 'ForgeSalamander', accessory: 'gear', color: 'amber', species: 'salamander' },
    ],
  },
}

export const FAMILY_IDS: AgentFamily[] = ['robot', 'orb', 'pet']

export function modelOf(look: AgentLook): AgentModel {
  const models = FAMILIES[look.family]?.models ?? FAMILIES.robot.models
  return models.find((m) => m.id === look.model) ?? models[0]!
}

export function colorOf(look: AgentLook): string {
  return AGENT_COLORS[look.color] ?? AGENT_COLORS[modelOf(look).color]!
}

/** A look that is one of ours, or null: what comes from storage or the server is checked, never trusted. */
export function validLook(value: unknown): AgentLook | null {
  if (!value || typeof value !== 'object') return null
  const v = value as Partial<AgentLook>
  if (!v.family || !FAMILY_IDS.includes(v.family)) return null
  if (!FAMILIES[v.family].models.some((m) => m.id === v.model)) return null
  return { family: v.family, model: v.model!, color: v.color && COLOR_IDS.includes(v.color) ? v.color : modelOf(v as AgentLook).color }
}

export function parsePreference(raw: string | null | undefined): AgentLookPreference {
  if (!raw) return { mode: 'random' }
  try {
    const value = JSON.parse(raw) as Partial<AgentLookPreference>
    const hat = value.hat && value.hat !== 'none' && (HATS as string[]).includes(value.hat) ? { hat: value.hat } : {}
    if (value.mode === 'fixed') {
      const look = validLook(value.look)
      return look ? { mode: 'fixed', look, ...hat } : { mode: 'random', ...hat }
    }
    if (value.mode === 'family' && value.family && FAMILY_IDS.includes(value.family)) return { mode: 'family', family: value.family, ...hat }
    return { mode: 'random', ...hat }
  } catch {
    // Not ours: the default.
  }
  return { mode: 'random' }
}

/** A small, stable hash: the same agent id gives the same pick, on every machine. */
export function hashOf(text: string): number {
  let h = 2166136261
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/**
 * The look of one agent: its own when it has one (an agent with a character of its own), else the person's
 * preference — fixed, random in one family, or random among all three — seeded by the agent's id.
 */
export function agentLookFor(agentId: string, preference?: AgentLookPreference | null, own?: AgentLook | null): AgentLook {
  if (own) return own
  const hat = preference?.hat ? { hat: preference.hat } : {}
  if (preference?.mode === 'fixed' && preference.look) return { ...preference.look, ...hat }
  const h = hashOf(agentId)
  const family = preference?.mode === 'family' && preference.family ? preference.family : FAMILY_IDS[h % FAMILY_IDS.length]!
  const models = FAMILIES[family].models
  const model = models[(h >>> 3) % models.length]!
  // Most agents wear their model's own colour; some another, so two of the same model can be told apart.
  const color = (h >>> 9) % 3 === 0 ? COLOR_IDS[(h >>> 11) % COLOR_IDS.length]! : model.color
  return { family, model: model.id, color, ...hat }
}

/** What an agent is doing, for the drawing — always from real data, never made up. */
export type AgentState = 'idle' | 'working' | 'waiting' | 'reviewing' | 'completed' | 'error' | 'offline'

export const AGENT_STATES: AgentState[] = ['idle', 'working', 'waiting', 'reviewing', 'completed', 'error', 'offline']

/**
 * The state of a session as the agent's drawing shows it: Planning, Analyzing, Coding and Testing are work;
 * Reviewing is reviewing; Blocked waits for someone; Completed is done; a failed session is an error; no session
 * reported (or the computer gone) is offline.
 */
export function agentStateOf(session: { activityState?: string | null; status?: string | null } | null | undefined): AgentState {
  if (!session) return 'offline'
  const status = (session.status ?? '').toLowerCase()
  if (status === 'failed') return 'error'
  if (status === 'completed') return 'completed'
  if (status === 'paused') return 'waiting'
  switch (session.activityState) {
    case 'Planning':
    case 'Analyzing':
    case 'Coding':
    case 'Testing':
      return 'working'
    case 'Reviewing':
      return 'reviewing'
    case 'Blocked':
      return 'waiting'
    case 'Completed':
      return 'completed'
    default:
      return 'idle'
  }
}
